import { Ring, RING_COLORS } from '@/components/ui/Ring';

export interface DashboardRingsProps {
  /** Calories consommées vs cible */
  kcal: { value: number; target: number };
  /** Protéines (g) consommées vs cible */
  protein: { value: number; target: number };
  /** Verres d'eau bus vs cible */
  water: { value: number; target: number };
  size?: number;
  thickness?: number;
}

/**
 * Dashboard rings façon Apple Watch — Calories / Protéines / Hydratation.
 * Présente les 3 anneaux concentriques + une légende compacte sous l'ensemble.
 *
 * Décision §6.F validée : 3 anneaux, pas de 4e par défaut. Layout testé pour
 * rester lisible sur 380px de largeur.
 */
export const DashboardRings = ({ kcal, protein, water, size = 168, thickness = 14 }: DashboardRingsProps) => {
  const kcalRatio = kcal.target ? kcal.value / kcal.target : 0;
  const proteinRatio = protein.target ? protein.value / protein.target : 0;
  const waterRatio = water.target ? water.value / water.target : 0;
  const gap = 4;

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative" style={{ width: size, height: size }}>
        <div className="absolute inset-0">
          <Ring value={kcalRatio} color={RING_COLORS.calories} size={size} thickness={thickness}
                ariaLabel={`Calories ${kcal.value} sur ${kcal.target}`} />
        </div>
        <div className="absolute inset-0">
          <Ring value={proteinRatio} color={RING_COLORS.protein} size={size} thickness={thickness}
                inset={thickness + gap}
                ariaLabel={`Protéines ${protein.value}g sur ${protein.target}g`} />
        </div>
        <div className="absolute inset-0">
          <Ring value={waterRatio} color={RING_COLORS.water} size={size} thickness={thickness}
                inset={(thickness + gap) * 2}
                ariaLabel={`Hydratation ${water.value} verres sur ${water.target}`} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Aujourd'hui</div>
            <div className="text-2xl font-light tabular-nums mt-0.5">
              {Math.round(Math.min(100, kcalRatio * 100))}%
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 w-full max-w-xs text-center">
        <Legend label="Kcal"      color={RING_COLORS.calories} value={`${kcal.value}`}    target={`/${kcal.target}`} />
        <Legend label="Protéines" color={RING_COLORS.protein}  value={`${protein.value}`} target={`/${protein.target}g`} />
        <Legend label="Eau"       color={RING_COLORS.water}    value={`${water.value}`}   target={`/${water.target}`} />
      </div>
    </div>
  );
};

const Legend = ({ label, color, value, target }: { label: string; color: string; value: string; target: string }) => (
  <div>
    <div className="flex items-center justify-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} aria-hidden="true" />
      <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
    </div>
    <div className="text-sm tabular-nums mt-0.5">
      <span className="font-medium">{value}</span>
      <span className="text-zinc-500">{target}</span>
    </div>
  </div>
);
